import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { homepageAPI, footerAPI, newsletterAPI } from '../../services/api';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  Youtube,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Loader2
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings, loading: settingsLoading, getLogoUrl } = useSiteSettings();
  
  const [newsletterData, setNewsletterData] = useState({
    title: 'Stay Updated with Our Latest News',
    description: 'Get the latest updates on our services, products, and industry insights.'
  });
  const [newsletterLoading, setNewsletterLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  
  // Footer data state
  const [footerData, setFooterData] = useState({
    settings: {
      footer_description: 'We are a leading digital agency specializing in web development, app development, and digital marketing solutions. Transform your business with our innovative technology solutions.',
      footer_copyright: '© 2025 Niswa Info Tech. All rights reserved.'
    },
    sections: [],
    socialLinks: []
  });
  const [footerLoading, setFooterLoading] = useState(true);
  
  // Debug logging removed for cleaner code

  // Fetch newsletter data from homepage API
  useEffect(() => {
    const fetchNewsletterData = async () => {
      try {
        setNewsletterLoading(true);
        const response = await homepageAPI.getContent();
        
        if (response?.data?.success) {
          const data = response.data.data;
          setNewsletterData({
            title: data.newsletter_title || 'Stay Updated with Our Latest News',
            description: data.newsletter_description || 'Get the latest updates on our services, products, and industry insights.'
          });
        }
      } catch (error) {
        console.error('Error fetching newsletter data:', error);
        // Keep default values if API fails
      } finally {
        setNewsletterLoading(false);
      }
    };

    fetchNewsletterData();
  }, []);

  // Fetch footer data
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        setFooterLoading(true);
        console.log('🔍 Fetching footer data...');
        const response = await footerAPI.getContent();
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🔍 Footer API response:', response);
        
        if (response?.data?.success) {
          setFooterData(response.data.data);
        }
      } catch (error) {
        console.error('❌ Error fetching footer data:', error);
        // Keep default values if API fails
      } finally {
        setFooterLoading(false);
      }
    };

    fetchFooterData();
  }, []);

  // Handle newsletter subscription
  const handleNewsletterSubscription = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setSubscriptionMessage('Please enter your email address.');
      return;
    }

    try {
      setSubscribing(true);
      setSubscriptionMessage('');
      
      const response = await newsletterAPI.subscribe({ email });
      
      if (response.data.success) {
        setSubscriptionMessage('Thank you for subscribing! Please check your email to confirm.');
        setEmail('');
      } else {
        setSubscriptionMessage(response.data.message || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      setSubscriptionMessage('Sorry, there was an error. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  // Helper function to get links by section type
  const getLinksBySection = (sectionType) => {
    if (!footerData.sections || footerData.sections.length === 0) {
      return [];
    }
    
    const section = footerData.sections.find(s => s.section_type === sectionType);
    return section ? section.links || [] : [];
  };


  return (
    <footer className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white overflow-hidden">
      {/* Decorative ambient blobs */}
      <div className="pointer-events-none absolute inset-0 opacity-20">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 blur-3xl"></div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-fuchsia-600 to-indigo-600 blur-3xl"></div>
      </div>
      {/* Newsletter Section */}
      <div className="relative py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center card-3d bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-indigo-600/20 backdrop-blur-md border border-white/10 rounded-3xl p-8 sm:p-12 shadow-3d">
            <h3 className="text-3xl md:text-4xl font-extrabold mb-4">
              {newsletterLoading ? <Loader2 className="w-8 h-8 animate-spin" /> : newsletterData.title}
            </h3>
            <p className="text-xl mb-10 text-white/80">
              {newsletterLoading ? <Loader2 className="w-6 h-6 animate-spin mr-2" /> : newsletterData.description}
            </p>
            
            <form onSubmit={handleNewsletterSubscription} className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-2xl text-white placeholder-white/60 bg-white/10 border border-white/20 backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-white/40"
                disabled={subscribing}
              />
              <button 
                type="submit"
                disabled={subscribing}
                className="card-3d bg-white text-gray-900 px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-glow"
              >
                {subscribing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
            
            {subscriptionMessage && (
              <div className={`mt-4 text-base ${subscriptionMessage.includes('Thank you') ? 'text-green-300' : 'text-red-300'}`}>
                {subscriptionMessage}
              </div>
            )}
          </div>
        </div>
      </div>
 
      {/* Main Footer Content */}
      <div className="relative py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-6">
                {(() => {
                  const logoUrl = !settingsLoading && getLogoUrl(true); // Use dark mode logo for footer
                  return logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt={!settingsLoading && settings?.siteTitle ? settings.siteTitle : 'Niswa Info Tech'} 
                      className="h-10 w-auto drop-shadow-lg"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-xl">N</span>
                    </div>
                  );
                })()}
                {/* Site title is hidden as requested */}
              </Link>
              <p className="text-white/70 mb-6 leading-relaxed">
                {footerLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  footerData.settings.footer_description || 'No description available'
                )}
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-blue-300" />
                  <span className="text-white/80">
                    {footerLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      footerData.settings.footer_email || 'No email available'
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-blue-300" />
                  <span className="text-white/80">
                    {footerLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      footerData.settings.footer_phone || 'No phone available'
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-blue-300" />
                  <span className="text-white/80">
                    {footerLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      footerData.settings.footer_address || 'No address available'
                    )}
                  </span>
                </div>
              </div>
            </div>
 
            {/* Footer Sections - Dynamic from API */}
            <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:col-span-4">
              {footerLoading ? (
                // Loading state
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="col-span-1">
                    <div className="h-6 bg-white/20 rounded mb-6 animate-pulse"></div>
                    <div className="space-y-3">
                      {Array.from({ length: 3 }).map((_, linkIndex) => (
                        <div key={linkIndex} className="h-4 bg-white/10 rounded animate-pulse"></div>
                      ))}
                    </div>
                  </div>
                ))
              ) : footerData.sections && footerData.sections.length > 0 ? (
                // Dynamic sections from API
                footerData.sections.map((section) => (
                  <div key={section.id} className="col-span-1">
                    <h4 className="text-lg font-bold mb-6 text-white">{section.title}</h4>
                    <ul className="space-y-3">
                      {section.links && section.links.length > 0 ? (
                        section.links.map((link) => (
                          <li key={link.id}>
                            {link.url ? (
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/70 hover:text-blue-300 transition-colors duration-200"
                              >
                                {link.title}
                              </a>
                            ) : (
                              <span className="text-white/50">{link.title}</span>
                            )}
                          </li>
                        ))
                      ) : (
                        <li className="text-white/50">No links available</li>
                      )}
                    </ul>
                  </div>
                ))
              ) : (
                // Fallback when no sections are available
                <div className="col-span-4 text-center text-white/50">
                  No footer sections available. Please add sections in the admin panel.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
 
      {/* Bottom Footer */}
      <div className="relative border-t border-white/10 py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white/70 text-sm mb-4 md:mb-0">
              {footerLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                footerData.settings.footer_copyright || 'No copyright available'
              )}
            </div>
            
            {/* Social Links */}
            <div className="flex items-center space-x-3">
              {footerData.socialLinks && footerData.socialLinks.length > 0 ? (
                footerData.socialLinks.map((social) => {
                  // Dynamic icon mapping
                  const getIconComponent = (iconName) => {
                    const iconMap = {
                      Facebook,
                      Twitter,
                      Instagram,
                      Linkedin,
                      Youtube
                    };
                    return iconMap[iconName] || Facebook;
                  };
                  
                  const IconComponent = getIconComponent(social.icon);
                  return (
                    <a
                      key={social.id}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 card-3d bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:scale-110 transition-all duration-300"
                      aria-label={social.name}
                    >
                      <IconComponent className="w-5 h-5" />
                    </a>
                  );
                })
              ) : (
                <div className="text-gray-400">No social links available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
